import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isOldEdge } from 'utils/utils';
import { isMobile, isTablet } from 'react-device-detect';
import usePrevious from 'hooks/usePrevious';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from "@amcharts/amcharts4/themes/animated"; // eslint-disable-line

import { CategoryFilterCriteria } from 'data/spending/selectors';

import QIconButton from 'components/QIconButton';
import QTip from 'components/QuickenControls/QTip';
import { formatAmount } from 'components/QCurrency/core';

import { makeStyles } from 'tss-react/mui';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Link from '@mui/material/Link';

const pieChartPropTypes = {
  id: PropTypes.string,
  spending: PropTypes.array,
  onSliceClick: PropTypes.func,
  getFilterCriteriaWithColor: PropTypes.func,
  filterHistory: PropTypes.object,
  onBreadcrumbClick: PropTypes.func,
  popFilterHistory: PropTypes.func,
  totalSpend: PropTypes.number,
  active: PropTypes.bool,
  isIncome: PropTypes.bool,
  isDashboard: PropTypes.bool,
  uncategorizedTransactions: PropTypes.object,
  categoryName: PropTypes.string,
  withEllipse: PropTypes.bool,
};

// const log = getLogger('PieChart');
const OMIT_CENTS = true;
const legendItemStyles = {
  flexBasis: '33%',
  textAlign: 'left',
  cursor: 'pointer',
  paddingLeft: 7,
  paddingRight: 7,
  marginBottom: 16,
  maxHeight: 42,
};

const useStyles = makeStyles()((theme) => {
  const breadcrumbLink = {
    ...theme.typography.subtitle2,
    background: 'transparent',
    border: 'none',
    outline: 0,
    color: theme.applyOpacityToHex(theme.palette.text.strong, 0.6),
    marginTop: 7,
    padding: 0,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: theme.applyOpacityToHex(theme.palette.text.strong, 0.6),
    },
    '&.isLastLeaf': {
      cursor: 'default',
    },
    '&.isLastLeaf:hover': {
      textDecoration: 'none',
    },
  };
  const summary = {
    ...theme.typography.h6,
    color: theme.applyOpacityToHex(theme.palette.text.strong, 0.87),
    textAlign: 'left',
    paddingBottom: 3,
    paddingTop: 6,
    borderBottom: `1px solid ${theme.palette.divider}`,
    lineHeight: '26px',
  };
  return ({
    tooltipRoot: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'space-between',
      width: 330,
      height: 104,
    },
    chartAndLegendWrapper: {
      display: 'flex',
      height: '100%',
    },
    chartAndLegendWrapperInDashboard: {
      display: 'flex',
      height: '100%',
      flexDirection: 'row',
      position: 'relative',
    },
    chartWrapper: {
      height: '100%',
    },
    chartWrapperInDashboard: {
      maxWidth: 172,
      minWidth: 150,
      height: 'auto',
      position: 'relative',
      top: 17,
      left: -12,
    },
    breadcrumbDivider: {
      ...theme.typography.body2,
      padding: '6px 6px 6px 5px',
      color: theme.applyOpacityToHex(theme.palette.text.strong, 0.6),
    },
    breadcrumbLink,
    breadcrumbLinkEllipsed: {
      ...breadcrumbLink,
      maxWidth: 211,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    legendRoot: {
      position: 'relative',
      height: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
    },
    legendWrapper: {
      width: '60%',
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'center',
    },
    legendWrapperInDashboard: {
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'center',
      height: 'auto',
      width: '100%',
      maxHeight: 100,
      marginTop: 0,
    },
    legendItemDashboard: {
      ...legendItemStyles,
      marginBottom: 12,
      '&:hover': {
        backgroundColor: theme.applyOpacityToHex(theme.palette.secondary.main, 0.08),
      },
    },
    legendItem: {
      ...legendItemStyles,
      minWidth: 230,
      '&:hover': {
        backgroundColor: theme.applyOpacityToHex(theme.palette.secondary.main, 0.08),
      },
      '@media (max-width: 1288px)': {
        minWidth: 174,
      },
      '@media (max-width: 1008px)': {
        minWidth: 154,
      },
      '@media (max-width: 905px)': {
        minWidth: 124,
      },
    },
    legendName: {
      ...(theme.typography.subtitle2),
      letterSpacing: '0.1px',
      color: theme.palette.text.strong,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    legendValue: {
      ...theme.typography.body2,
      letterSpacing: '0.3px',
      color: theme.palette.text.strong,
    },
    summary,
    summaryEllipsed: {
      ...summary,
      maxWidth: 311,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    resetToAllCategoriesButton: {
      display: 'inline-block',
      marginRight: 6,
      borderRadius: '14px',
      height: 16,
      width: 16,
      background: theme.palette.background.subtle,
      position: 'relative',
      top: 4,
    },
    pieAndCategorize: {
      width: '40%',
      position: 'relative',
    },
    unCatLabelRoot: (props) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      ...(props.isDashboard ? { maxWidth: 130 } : {}),
    }),
    unCatLabelSmallChart: {
      position: 'absolute',
      bottom: 40,
      left: 0,
    },
    unCatLabelBigChart: {
      position: 'absolute',
      bottom: 8,
      left: '50%',
      transform: 'translateX(-50%)',
    },
  });
});

function PieChart(props) {
  const { id, spending: theSpending, filterHistory: theFilterHistory, popFilterHistory, uncategorizedTransactions, onBreadcrumbClick, totalSpend,
    isIncome, isDashboard, active, onSliceClick, getFilterCriteriaWithColor, withEllipse, categoryName } = props;
  const { classes, theme } = useStyles();

  const spendingRef = useRef(null);
  const chartRef = useRef(null);
  const filterHistoryRef = useRef(theFilterHistory);
  const [dummy, setDummy] = useState(false);
  let spending;
  let filterHistory;
  const prevSpending = usePrevious(theSpending);

  const hideTooltips = () => {
    if (chartRef.current?.series.values[0].slices) {
      chartRef.current.series.values[0].slices.each((slice) => {
        slice.hideTooltip();
      });
    }
  };

  const sliceClick = (e, index) => {
    // tracker.track(tracker.events.spendingDrillDown, { type: 'pie' });
    e?.event?.stopPropagation();
    const indexToUse = e ? e.target.dataItem.index : index;
    spending = spendingRef.current;
    filterHistory = filterHistoryRef.current;

    hideTooltips();  // prevents an orphaned tooltip on the next render

    // prevent drill down if we're on last leaf
    const currentItemName = filterHistory.size > 1 ? filterHistory.get(filterHistory.size - 1).name : '';
    if (currentItemName && currentItemName !== 'Everything Else' && (spending && spending.length === 1 && spending[0].coaPathName === currentItemName)) {
      return;
    }

    const { filterCriteria, color } = spending?.[indexToUse] ?? {};
    if (filterCriteria && filterCriteria.topLevelOnly) {
      return;
    }

    // This is so twe can keep the color after clicking a slice or legend item
    const filterCriteriaWithColor = getFilterCriteriaWithColor(filterCriteria, color);

    if (onSliceClick) {
      onSliceClick(filterCriteriaWithColor);
    }
  };

  const setChartData = (theChart, theData) => {
    const isValidAmount = (amount) => isIncome ? amount > 0 : amount < 0;
    if (theData) {
      // TODO: greycommented out for now to see if eventDispatcher bug decreases, current version is 2.2.0
      // chartRef.current.events.once('datavalidated',() => {
      //   chartRef.current.series.each((theSeries) => active ? theSeries.appear() : theSeries.hide(0));
      // });
      theChart.data = theData.filter((o) => o.excludeFromGraph !== true && isValidAmount(o.total)).map((o) => ({  // eslint-disable-line
        currency: o.currency,
        total: formatAmount(isIncome ? o.total : -o.total, o.currency, OMIT_CENTS),
        x: o.x,
        y: isIncome ? o.total : -o.total,
        filterCriteria: o.filterCriteria,
        color: o.color,
      }));
    }
  };

  const makeChart = () => {
    am4core.useTheme(am4themes_animated);
    am4core.options.autoDispose = true;
    chartRef.current = am4core.create(id, am4charts.PieChart);

    const pieSeries = chartRef.current.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = 'total';
    pieSeries.dataFields.category = 'x';
    pieSeries.colorField = 'color';
    pieSeries.hiddenState.properties.endAngle = -90;  // accomplishes the "fan out" animation
    pieSeries.slices.template.showOnInit = true;
    const activeState = pieSeries.slices.template.states.getKey('active');
    activeState.properties.shiftRadius = 0;
    chartRef.current.innerRadius = am4core.percent(35); // cut a hole in our Pie chart

    pieSeries.slices.template.strokeWidth = 0;
    pieSeries.slices.template.strokeOpacity = 0;
    pieSeries.slices.template.strokeOpacity = 0;
    pieSeries.slices.template.tooltipText = '[font-size: 14px]{category}\n{total}[/]';

    const { tooltip } = pieSeries;
    tooltip.pointerOrientation = 'left';
    
    chartRef.current.tooltipContainer.htmlContainer.style.zIndex = '10000';

    // change the cursor on hover to make it apparent the object can be interacted with
    pieSeries.slices.template.cursorOverStyle = [{
      property: 'cursor',
      value: 'pointer',
    }];
    pieSeries.slices.template.events.on('hit', sliceClick);
    pieSeries.slices.template.adapter.add('fill', (fill, target) => (
      target.dataItem && target.dataItem.index >= 0 ? target.dataItem._dataContext.color : fill // eslint-disable-line
    ));
    pieSeries.alignLabels = false;
    pieSeries.labels.template.bent = false;
    pieSeries.labels.template.radius = 110000;
    pieSeries.labels.template.padding(0, 0, 0, 0);
    pieSeries.ticks.template.disabled = true;

    // center label
    pieSeries.centerLabel = pieSeries.createChild(am4core.Label);
    pieSeries.centerLabel.horizontalCenter = 'middle';
    pieSeries.centerLabel.verticalCenter = 'middle';
    pieSeries.centerLabel.textAlign = 'middle';
    pieSeries.centerLabel.fontSize = (isTablet || isMobile || isDashboard) ? 11 : 20;
    pieSeries.centerLabel.fill = am4core.color(theme.palette.text.primary);

    if (!isOldEdge()) {
      // Create a base filter effect (as if it's not there) for the hover to return to
      const shadow = pieSeries.slices.template.filters.push(new am4core.DropShadowFilter());
      shadow.opacity = 0;

      // Create hover state
      const hoverState = pieSeries.slices.template.states.getKey('hover'); // normally we have to create the hover state, in this case it already exists

      // Slightly shift the shadow and make it more prominent on hover
      const hoverShadow = hoverState.filters.push(new am4core.DropShadowFilter());
      hoverShadow.opacity = 0.7;
      hoverShadow.blur = 5;
    }
    setChartData(chartRef.current, spendingRef.current);
  };

  useEffect(() => { // componentDidMount
    spendingRef.current = theSpending;
    filterHistoryRef.current = theFilterHistory;
    makeChart();
    chartRef.current.series.each((series) => active ? series.appear() : series.hide(0));
    return () => chartRef.current ? chartRef.current?.dispose?.() : null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // clear filter history when spending has no txns due to an edit on the last remaining txn
    spendingRef.current = theSpending;
    filterHistoryRef.current = theFilterHistory;
    makeChart();
    setDummy(!dummy); // force a re-render
    if (prevSpending && prevSpending.length !== 0 && spending.length === 0) {
      popFilterHistory();
    }
  }, [theSpending, theFilterHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chartRef.current.series.each((series) => active ? series.appear() : series.hide(0));
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const legendItemClick = (e, item) => {
    e.stopPropagation();
    sliceClick(null, item);
  };

  const legendItemMouseOver = (item) => {
    if (chartRef.current.series.values[0].slices.values[item]) {
      chartRef.current.series.values[0].slices.values[item].isHover = true;
    }
  };

  const legendItemMouseOut = (item) => {
    if (chartRef.current.series.values[0].slices.values[item]) {
      chartRef.current.series.values[0].slices.values[item].isHover = false;
    }
  };

  const handleCategorizeUncategorized = (e) => {
    e.stopPropagation();
    onSliceClick(new CategoryFilterCriteria('0', null, 'Uncategorized'));
  };

  //----------------------------------------------------------------------------------
  //
  //----------------------------------------------------------------------------------
  spending = spendingRef.current;
  filterHistory = filterHistoryRef.current;

  const numberOfUncategorizedTnxs = uncategorizedTransactions && uncategorizedTransactions.txns && uncategorizedTransactions.txns.length;
  const displayWebFirstCategorizebtn = numberOfUncategorizedTnxs && categoryName !== 'Uncategorized' && filterHistory.size === 1;
  const currentItemName = filterHistory.size > 1 ? filterHistory.get(filterHistory.size - 1).name : '';

  let displayedTotal = 0;
  const chartData = chartRef.current?.data || null;
  if (chartData && chartData.length > 1) {
    displayedTotal = `${formatAmount(isIncome ? totalSpend : -totalSpend, chartData[0].currency, OMIT_CENTS)}`;
  } else if (chartData && chartData.length === 1) {
    displayedTotal = chartData[0].total;
  }
  if (chartRef && chartRef.current) {
    chartRef.current.series.values[0].centerLabel.text = `TOTAL\n${formatAmount(isIncome ? totalSpend : -totalSpend, chartRef.current.data[0]?.currency || 'USD', OMIT_CENTS)}`;
  }

  // when we reach the last leaf, disable clicks on it
  let isDrillable = true;
  if (currentItemName && currentItemName !== 'Everything Else' && (spending && spending.length === 1 && spending[0].coaPathName === currentItemName)) {
    isDrillable = false;
  }

  return (
    <div className={isDashboard ? classes.chartAndLegendWrapperInDashboard : classes.chartAndLegendWrapper}>
      <div className={!isDashboard ? classes.pieAndCategorize : null}>
        <div id={id} className={isDashboard ? classes.chartWrapperInDashboard : classes.chartWrapper} />
        {displayWebFirstCategorizebtn && (
          <div className={isDashboard ? classes.unCatLabelSmallChart : classes.unCatLabelBigChart}>
            <QTip
              title="Quicken recommends you to categorize all your transactions so you know exactly
              where your money is spent. This will also give a correct picture in your budgets and reports"
              placement="right-start"
              popperProps={{
                modifiers: {
                  offset: {
                    enabled: true,
                    offset: '-40,0',
                  },
                  flip: {
                    enabled: false,
                  },
                },
              }}
            >
              <Link component="button" variant="caption" underline="hover" onClick={handleCategorizeUncategorized}>
                <div className={classes.unCatLabelRoot}>
                  <i className="material-icons" style={{ paddingRight: 3 }}>info</i>
                  <span className={classes.categorizeBtn}>{numberOfUncategorizedTnxs} Uncategorized transaction{numberOfUncategorizedTnxs > 1 ? 's' : '' }</span>
                </div>
              </Link>
            </QTip>
          </div>
        )}
      </div>
      <div className={isDashboard ? classes.legendWrapperInDashboard : classes.legendWrapper} id="legend-wrapper">
        {!isDashboard &&
        <div style={{ textAlign: 'left', marginBottom: 20 }}>
          {filterHistory.map((item, i) => {
            const split = item.name.split(':');
            const itemName = split.length > 1 ? split[split.length - 1] : item.name;
            return (
              <span key={`${itemName}-${i.toString()}`}>
                {i === 0 && filterHistory.size > 1 &&
                <span key="breadcrumb-reset" className={classes.resetToAllCategoriesButton}>
                  <QTip title="Go back">
                    <QIconButton
                      size="small-no-padding"
                      aria-label="Go back"
                      onClick={popFilterHistory}
                      id="breadcrumbs-back-button"
                      style={{ position: 'relative', top: -5, left: 0, fontSize: '16px' }}
                    >
                      <ChevronLeftIcon />
                    </QIconButton>
                  </QTip>
                </span>}
                <span>
                  {i > 0 &&
                  <span className={classes.breadcrumbDivider}>/</span>}
                  <button
                    className={classNames(withEllipse ? classes.breadcrumbLinkEllipsed : classes.breadcrumbLink, { isLastLeaf: i === filterHistory.size - 1 })}
                    data-index={i}
                    disabled={filterHistory.size === 1}
                    onClick={onBreadcrumbClick}
                    type="button"
                  >
                    {itemName}
                  </button>
                </span>
              </span>
            );
          })}
          {currentItemName && currentItemName !== 'Everything Else' &&
          <div className={withEllipse ? classes.summaryEllipsed : classes.summary}>
            <div style={{ display: 'inline', whiteSpace: 'nowrap' }}>{`${currentItemName}`}</div>
            {chartData && chartData[0] &&
            <div>
              {displayedTotal}
            </div>}
          </div>}
        </div>}
        <div id="pie-chart-legend-div" className={classes.legendRoot}>
          {spendingRef.current && spendingRef.current.filter((o, i) => i < 5).map((o, i) => (
            o.total !== 0 && 
            <div
              role="presentation"
              className={isDashboard ? classes.legendItemDashboard : classes.legendItem}
              id={`legend-item-${o.x}`}
              key={`legend-item-${o.x}-${o.y}`}
              onClick={(e) => legendItemClick(e, i)}
              onMouseOver={() => legendItemMouseOver(i)}
              onFocus={() => legendItemMouseOver(i)}
              onMouseOut={() => legendItemMouseOut(i)}
              onBlur={() => legendItemMouseOut(i)}
              style={{
                borderLeft: `4px solid ${o.color}`,
                width: isDashboard ? '33%' : '305px',
                flexBasis: isDashboard ? 'initial' : '33%',
                pointerEvents: isDrillable ? 'auto' : 'none',
              }}
            >
              <div className={classes.legendName}>{o.x}</div>
              <div className={classes.legendValue}>{
                formatAmount(isIncome ? o.total : -o.total, o.currency, OMIT_CENTS)
              }
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

PieChart.propTypes = pieChartPropTypes;

PieChart.whyDidYouRender = true;

export default PieChart;




















